import { useCallback, useEffect, useRef, useState } from 'react'

const getAppleMusicPreviewUrl = async (
  trackId: string,
  country: string = 'us',
): Promise<string | null> => {
  try {
    // Use iTunes Search API to get track information including preview URL
    const response = await fetch(
      `https://itunes.apple.com/lookup?id=${trackId}&country=${country}&entity=song`,
    )
    const data = await response.json()
    if (data.results && data.results.length > 0) {
      const track = data.results[0]
      return track.previewUrl || null
    }
    return null
  } catch (error) {
    console.error('Failed to fetch Apple Music preview URL:', error)
    return null
  }
}

// Apple Music Player state interface
interface AppleMusicPlayerState {
  playerId: string
  previewUrl: string | null
  duration: number
  currentTime: number
  isPlaying: boolean
  isReady: boolean
  error: string | null
  togglePlayPause: () => void
  seekTo: (_time: number) => void
  pause: () => void
}

// Global cache for preview URLs to avoid repeated API calls
let previewUrlCache: { [key: string]: string | null } = {}

/**
 * Hook for managing Apple Music player using 30-second preview clips
 * Similar to AIMS implementation - uses iTunes Search API preview URLs with HTML5 audio
 */
export const useAppleMusicPlayer = (
  trackId: string | null,
  country: string = 'us',
): AppleMusicPlayerState => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [duration, setDuration] = useState(30) // Apple Music previews are 30 seconds
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const playerIdRef = useRef(`apple-music-player-${Date.now()}`)
  const isInitializedRef = useRef(false)
  const currentTrackIdRef = useRef<string | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Manual time update interval when playing (backup for timeupdate events)
  useEffect(() => {
    if (isPlaying && audioRef.current && isReady) {
      timeUpdateIntervalRef.current = setInterval(() => {
        if (audioRef.current && !audioRef.current.paused) {
          const newTime = audioRef.current.currentTime
          setCurrentTime(newTime)
        }
      }, 100) // Update every 100ms
    } else {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }
    }
  }, [isPlaying, isReady])

  useEffect(() => {
    if (!trackId) return

    let isMounted = true

    const initPlayer = async () => {
      try {
        // Prevent double initialization for the same track
        if (isInitializedRef.current && currentTrackIdRef.current === trackId) {
          // Reset position to 0 when reusing existing player
          if (audioRef.current) {
            audioRef.current.pause() // Actually pause the audio element
            audioRef.current.currentTime = 0
            setCurrentTime(0)
            setIsPlaying(false)
          }
          return
        }

        // If track changed, reset initialization
        if (currentTrackIdRef.current !== trackId) {
          isInitializedRef.current = false
          currentTrackIdRef.current = trackId
        }

        // Check cache first
        const cacheKey = `${trackId}-${country}`
        let previewUrlFromApi = previewUrlCache[cacheKey]

        if (previewUrlFromApi === undefined) {
          // Fetch preview URL from iTunes Search API
          previewUrlFromApi = await getAppleMusicPreviewUrl(trackId, country)
          previewUrlCache[cacheKey] = previewUrlFromApi // Cache the result
        }

        if (!previewUrlFromApi) {
          throw new Error('No preview URL available for this track')
        }

        if (!isMounted) {
          return
        }

        setPreviewUrl(previewUrlFromApi)

        // Create or update HTML5 audio element
        if (!audioRef.current) {
          audioRef.current = new Audio(previewUrlFromApi)
        } else {
          audioRef.current.src = previewUrlFromApi
        }

        const audio = audioRef.current

        // Set up event listeners
        const handleLoadedData = () => {
          if (!isMounted) return
          setDuration(audio.duration || 30)
          setIsReady(true)
          setError(null)
          isInitializedRef.current = true
        }

        const handleTimeUpdate = () => {
          if (!isMounted) return
          setCurrentTime(audio.currentTime)
        }

        const handlePlay = () => {
          if (!isMounted) return
          setIsPlaying(true)
        }

        const handlePause = () => {
          if (!isMounted) return
          setIsPlaying(false)
        }

        const handleEnded = () => {
          if (!isMounted) return
          setIsPlaying(false)
          setCurrentTime(0)
        }

        const handleError = (_e: any) => {
          setError('Failed to load preview audio')
        }

        // Add event listeners
        audio.addEventListener('loadeddata', handleLoadedData)
        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('play', handlePlay)
        audio.addEventListener('pause', handlePause)
        audio.addEventListener('ended', handleEnded)
        audio.addEventListener('error', handleError)

        // Load the audio
        audio.load()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    }

    initPlayer()

    return () => {
      isMounted = false

      // Clean up audio element
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.removeEventListener('loadeddata', () => {})
        audioRef.current.removeEventListener('timeupdate', () => {})
        audioRef.current.removeEventListener('play', () => {})
        audioRef.current.removeEventListener('pause', () => {})
        audioRef.current.removeEventListener('ended', () => {})
        audioRef.current.removeEventListener('error', () => {})
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }
    }
  }, [trackId, country])

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || !isReady) {
      return
    }

    try {
      if (isPlaying) {
        audioRef.current.pause()

        // Force update React state if audio is actually paused (similar to play fix)
        setTimeout(() => {
          if (audioRef.current && audioRef.current.paused) {
            setIsPlaying(false)
          }
        }, 50)
      } else {
        const playPromise = audioRef.current.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Force update React state if audio is actually playing
              if (audioRef.current && !audioRef.current.paused) {
                setIsPlaying(true)
              }
            })
            .catch(error => {
              console.error('[useAppleMusicPlayer] Playback failed:', error)
              setError('Unable to play this preview.')
            })
        }
      }
    } catch (err) {
      console.error('[useAppleMusicPlayer] Toggle play/pause error:', err)
      setError('Unable to play this preview')
    }
  }, [isPlaying, isReady])

  const seekTo = useCallback(
    (time: number) => {
      if (!audioRef.current || !isReady) return

      try {
        audioRef.current.currentTime = time
        setCurrentTime(time)
      } catch (err) {
        // Ignore seek errors
      }
    },
    [isReady],
  )

  const pause = useCallback(() => {
    if (!audioRef.current) {
      return
    }
    try {
      audioRef.current.pause()
    } catch (err) {
      console.error('[useAppleMusicPlayer] Pause error:', err)
    }
  }, [])

  return {
    playerId: playerIdRef.current,
    previewUrl,
    duration,
    currentTime,
    isPlaying,
    isReady,
    error,
    togglePlayPause,
    seekTo,
    pause,
  }
}
